import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Home, Lock, Eye, EyeOff, CheckCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { resetPassword } from '../services/passwordResetApi';
import { useToast } from '../components/Toast';
import { useI18n } from '../contexts/I18nContext';

const ResetPassword: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      showError('Token de recuperação não encontrado.');
      setTimeout(() => navigate('/forgot-password'), 2000);
    }
  }, [searchParams, navigate, showError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      showError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('As senhas não coincidem.');
      return;
    }

    if (!token) {
      showError('Token inválido.');
      return;
    }

    setIsLoading(true);

    try {
      await resetPassword(token, newPassword);
      setIsSuccess(true);
      showSuccess('Senha redefinida com sucesso! Redirecionando para o login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (error: any) {
      console.error('Erro ao redefinir senha:', error);
      showError(error?.response?.data?.error || 'Erro ao redefinir senha. O token pode ter expirado.');
    } finally {
      setIsLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #6b21a8, #7c3aed, #a855f7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'white',
    borderRadius: '1rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '28rem',
    padding: '2rem',
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '2rem',
  };

  const titleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  };

  const inputGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.25rem',
  };

  const inputContainerStyle: React.CSSProperties = {
    position: 'relative',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    paddingLeft: '2.5rem',
    paddingRight: '3rem',
    paddingTop: '0.75rem',
    paddingBottom: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    outline: 'none',
  };

  const iconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    height: '1.25rem',
    width: '1.25rem',
    color: '#9ca3af',
  };

  const eyeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#9ca3af',
  };

  const submitButtonStyle: React.CSSProperties = {
    width: '100%',
    backgroundColor: '#9333ea',
    color: 'white',
    padding: '0.75rem 1rem',
    borderRadius: '0.5rem',
    fontWeight: '500',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  };

  const successStyle: React.CSSProperties = {
    padding: '1rem',
    backgroundColor: '#dcfce7',
    border: '1px solid #86efac',
    borderRadius: '0.5rem',
    color: '#166534',
    textAlign: 'center',
  };

  const linkStyle: React.CSSProperties = {
    color: '#9333ea',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    marginTop: '1rem',
  };

  if (isSuccess) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={successStyle}>
            <CheckCircle size={32} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem', fontWeight: '600' }}>{t('resetPassword.successTitle')}</h3>
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              Sua senha foi redefinida com sucesso. Você será redirecionado para o login.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={titleStyle}>
            <Home style={{ height: '3rem', width: '3rem', color: '#9333ea', marginRight: '0.75rem' }} />
            <h1 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>{t('login.appName')}</h1>
          </div>
          <p style={{ color: '#4b5563', margin: 0 }}>{t('login.tagline')}</p>
        </div>

        {/* Content */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>{t('resetPassword.titleFull')}</h2>
          <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0' }}>{t('resetPassword.placeholder')}</p>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {/* New Password */}
          <div style={inputGroupStyle}>
            <label htmlFor="reset-password-new" style={labelStyle}>{t('resetPassword.newPassword')}</label>
            <div style={inputContainerStyle}>
              <Lock style={iconStyle} />
              <input
                id="reset-password-new"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={inputStyle}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                minLength={6}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={eyeButtonStyle}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showPassword ? <EyeOff style={{ height: '1.25rem', width: '1.25rem' }} /> : <Eye style={{ height: '1.25rem', width: '1.25rem' }} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div style={inputGroupStyle}>
            <label htmlFor="reset-password-confirm" style={labelStyle}>{t('resetPassword.confirmNew')}</label>
            <div style={inputContainerStyle}>
              <Lock style={iconStyle} />
              <input
                id="reset-password-confirm"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={inputStyle}
                placeholder="••••••••"
                autoComplete="new-password"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={eyeButtonStyle}
                aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                {showConfirmPassword ? <EyeOff style={{ height: '1.25rem', width: '1.25rem' }} /> : <Eye style={{ height: '1.25rem', width: '1.25rem' }} />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || !token}
            style={submitButtonStyle}
            onMouseOver={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#7c3aed')}
            onMouseOut={(e) => !isLoading && (e.currentTarget.style.backgroundColor = '#9333ea')}
          >
            {isLoading ? (
              <>
                <Loader2 className="spin" size={16} />
                Redefinindo...
              </>
            ) : (
              <>
                <CheckCircle size={16} />{t('resetPassword.titleFull')}</>
            )}
          </button>
        </form>

        {/* Footer */}
        <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <Link to="/login" style={linkStyle}>
            <ArrowLeft size={16} />
            Voltar para o login
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        input:focus {
          outline: 2px solid transparent;
          outline-color: #a855f7;
          border-color: transparent;
        }
        button:focus {
          outline: 2px solid transparent;
          outline-offset: 2px;
        }
        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ResetPassword;

